import React, { useState } from 'react';
import { ArrowLeft } from 'lucide-react';
import MarketingHeader from '../components/MarketingHeader';
import { REQUEST_ACCESS_URL } from '../config/links';
import LegalFooter from '../components/LegalFooter';
import useDocumentMeta from '../hooks/useDocumentMeta';

/* /start — the landing the physical Factory OS notebook QR codes point to.
   Someone sketched on graph paper, scanned the QR on the page, and arrived
   here. The page's whole job: show that the sketch just became a structured
   node in their engineering memory, and that their first 100 nodes are free.
   Uses the same notebook design system and the same motor-mount scenario as
   the homepage so it reads like one connected product. */

const ACCENT = 'var(--nb-accent)';
const GREEN = 'var(--nb-green)';
const mono = (size, color) => ({ fontFamily: 'var(--mono)', fontSize: size, color });

/* The centerpiece: a page of graph paper with a hand sketch and a QR corner,
   that transforms into a structured node when "scanned." Paper → node is the
   signature move of the whole product, so we let the visitor trigger it. */
const ScanToNode = () => {
  const [scanned, setScanned] = useState(false);

  return (
    <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_1fr] gap-4 sm:gap-5 items-center">
      {/* The paper page */}
      <div
        style={{
          position: 'relative',
          border: '1.5px solid var(--ink)',
          borderRadius: 4,
          background:
            'repeating-linear-gradient(0deg, transparent, transparent 15px, rgba(0,0,0,0.05) 15px, rgba(0,0,0,0.05) 16px), repeating-linear-gradient(90deg, transparent, transparent 15px, rgba(0,0,0,0.05) 15px, rgba(0,0,0,0.05) 16px), var(--paper)',
          boxShadow: '4px 5px 0 rgba(0,0,0,0.12)',
          padding: '16px 16px 14px',
          minHeight: 208,
          overflow: 'hidden',
        }}
      >
        {/* QR corner */}
        <div
          style={{
            position: 'absolute',
            top: 10,
            right: 10,
            width: 34,
            height: 34,
            border: '1.5px solid var(--ink)',
            borderRadius: 3,
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gridTemplateRows: 'repeat(4, 1fr)',
            gap: 1,
            padding: 3,
            background: 'var(--paper)',
          }}
          aria-hidden
        >
          {[1, 0, 1, 1, 0, 1, 0, 1, 1, 1, 1, 0, 0, 1, 0, 1].map((on, i) => (
            <div key={i} style={{ background: on ? 'var(--ink)' : 'transparent', borderRadius: 0.5 }} />
          ))}
        </div>

        <div style={{ fontFamily: 'var(--script)', fontSize: 'var(--t-md)', color: 'var(--ink)', lineHeight: 1 }}>
          motor mount bracket
        </div>
        <div style={{ ...mono('var(--t-sm)', 'var(--ink-3)'), marginTop: 4 }}>sketch · pg 14</div>

        {/* A rough hand sketch */}
        <svg viewBox="0 0 180 96" style={{ width: '100%', maxWidth: 210, marginTop: 12 }} aria-hidden>
          <path
            d="M18,74 L18,30 Q18,24 24,24 L72,24 Q78,24 78,30 L78,50 L150,50 Q158,50 158,58 L158,74"
            fill="none"
            stroke="var(--ink)"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <circle cx="33" cy="66" r="4" fill="none" stroke="var(--ink)" strokeWidth="1.5" />
          <circle cx="143" cy="66" r="4" fill="none" stroke="var(--ink)" strokeWidth="1.5" />
          {/* dimension line */}
          <path d="M18,88 L158,88" stroke="var(--ink-3)" strokeWidth="1" />
          <path d="M18,84 L18,92 M158,84 L158,92" stroke="var(--ink-3)" strokeWidth="1" />
          <text x="74" y="85" style={{ fontFamily: 'var(--mono)', fontSize: 'var(--t-sm)', fill: 'var(--ink-3)' }}>
            120 mm
          </text>
        </svg>

        <div style={{ position: 'absolute', bottom: 10, left: 16, fontFamily: 'var(--script)', fontSize: 'var(--t-md)', color: ACCENT }}>
          6061 — fatigue at fillet?
        </div>

        {/* scan sweep */}
        {scanned && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background: 'linear-gradient(180deg, transparent 0%, rgba(193,74,26,0.18) 50%, transparent 100%)',
              animation: 'scanSweep 0.7s ease-out 1',
            }}
            aria-hidden
          />
        )}
      </div>

      {/* Arrow / action */}
      <div className="flex sm:flex-col items-center gap-2 justify-center py-1">
        <button
          onClick={() => setScanned((s) => !s)}
          className="nb-btn"
          style={{ whiteSpace: 'nowrap', fontSize: 'var(--t-sm)', padding: '8px 14px' }}
        >
          {scanned ? '↻ Again' : '⌕ Scan page'}
        </button>
        <span style={{ ...mono('var(--t-md)', 'var(--ink-4)') }} className="hidden sm:block" aria-hidden>↓</span>
        <span style={{ ...mono('var(--t-md)', 'var(--ink-4)') }} className="sm:hidden" aria-hidden>→</span>
      </div>

      {/* The resulting node */}
      <div
        style={{
          border: `1.5px solid ${scanned ? 'var(--ink)' : 'var(--ink-4)'}`,
          borderRadius: 4,
          background: 'var(--paper)',
          boxShadow: scanned ? '4px 5px 0 rgba(0,0,0,0.12)' : 'none',
          padding: 0,
          minHeight: 208,
          opacity: scanned ? 1 : 0.4,
          transform: scanned ? 'translateY(0)' : 'translateY(4px)',
          transition: 'opacity 0.4s ease 0.35s, transform 0.4s ease 0.35s, border-color 0.3s ease',
          overflow: 'hidden',
        }}
      >
        <div style={{ padding: '9px 13px', borderBottom: '1.5px solid var(--ink-4)', background: 'var(--accent-wash)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ ...mono('var(--t-sm)', ACCENT), fontWeight: 700, letterSpacing: '0.6px', textTransform: 'uppercase' }}>Node · created</span>
          <span style={mono('var(--t-sm)', 'var(--ink-3)')}>from pg 14</span>
        </div>
        <div style={{ padding: '13px' }}>
          <div style={{ fontFamily: 'var(--hand)', fontWeight: 700, fontSize: 'var(--t-md)', color: 'var(--ink)' }}>Motor Mount Bracket</div>
          <div style={{ ...mono('var(--t-sm)', 'var(--ink-3)'), marginTop: 2 }}>MECH · Rev A · owner you</div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 11 }}>
            {[
              ['Material', '6061-T6'],
              ['Requirement', 'Survive 5×10⁵ cycles'],
              ['Open question', 'Fatigue at fillet?'],
            ].map(([k, v]) => (
              <div key={k} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, borderBottom: '1px dotted var(--ink-4)', paddingBottom: 5 }}>
                <span style={mono('var(--t-sm)', 'var(--ink-3)')}>{k}</span>
                <span style={{ fontFamily: 'var(--hand)', fontSize: 'var(--t-md)', color: 'var(--ink)', fontWeight: 700, textAlign: 'right' }}>{v}</span>
              </div>
            ))}
          </div>

          <div style={{ ...mono('var(--t-sm)', GREEN), fontWeight: 700, marginTop: 11 }}>✓ structured · searchable · connected</div>
        </div>
      </div>
    </div>
  );
};

const StartPage = () => {

  useDocumentMeta({
    title: 'The free Factory OS notebook — Start free | The Engineering Operating System',
    description:
      'The free Factory OS notebook looks like graph paper and works like engineering memory. Sketch a part, scan the QR on the page, and it opens in the app already structured into a node — searchable, connected, and permanent. Your first 100 nodes are free for every organization. A node is a part, sub-assembly, assembly, or product.',
    path: '/start',
  });

  return (
    <div className="fos-notebook min-h-screen">
      <MarketingHeader />

      {/* ══════════ HERO ══════════ */}
      <section className="pt-14 sm:pt-20 pb-10 sm:pb-14 px-5">
        <div className="max-w-[1080px] mx-auto">
          <a href="/" className="inline-flex items-center gap-2 no-underline mb-8" style={{ ...mono('var(--t-sm)', 'var(--ink-3)'), letterSpacing: '0.4px' }}>
            <ArrowLeft size={14} /> Back to Factory OS
          </a>

          <div className="mb-5">
            <span className="nb-eyebrow" style={{ color: ACCENT }}>
              the free factory os notebook
            </span>
          </div>

          <h1 style={{ fontFamily: 'var(--hand)', fontWeight: 700, fontSize: 'var(--t-lg)', lineHeight: 1.05, color: 'var(--ink)', letterSpacing: '-0.5px' }}>
            Sketch on paper.<br />
            <span className="nb-under">Keep it forever.</span>
          </h1>

          <p className="mt-5 max-w-[640px]" style={{ fontFamily: 'var(--hand)', fontSize: 'var(--t-md)', color: 'var(--ink-2)', lineHeight: 1.45 }}>
            The free Factory OS notebook looks like graph paper and works like engineering memory. Sketch a part, scan the QR on the page, and it opens in the app already structured into a node — searchable, connected, and permanent. No forms. No re-typing. The record falls out of the work.
          </p>

          <p className="mt-3 max-w-[640px]" style={{ fontFamily: 'var(--script)', color: ACCENT, fontSize: 'var(--t-md)', lineHeight: 1.2 }}>
            Sketch like paper. Remember like a machine.
          </p>

          <div className="flex flex-wrap gap-3 mt-7">
            <a href={REQUEST_ACCESS_URL} className="nb-btn no-underline text-center">Claim your free notebook</a>
            <a href="https://demo.factory-os.com" className="nb-btn nb-btn-ghost">▶ See the 2-minute tour</a>
          </div>

          <div className="mt-4" style={{ ...mono('var(--t-sm)', 'var(--ink-3)'), letterSpacing: '0.4px' }}>
            your first 100 nodes are free · for any organization
          </div>
        </div>
      </section>

      {/* ══════════ THE TRANSFORM ══════════ */}
      <section className="py-12 md:py-[64px] px-5" style={{ borderTop: '1px dashed var(--ink-4)' }}>
        <div className="max-w-[1080px] mx-auto">
          <div className="max-w-[720px] mb-8">
            <div className="nb-eyebrow" style={{ color: ACCENT }}>paper in, node out</div>
            <h2 className="mt-2" style={{ fontFamily: 'var(--hand)', fontWeight: 700, fontSize: 'var(--t-lg)', lineHeight: 1.1, color: 'var(--ink)' }}>
              Scan a page. Watch it become a node.
            </h2>
            <p className="mt-3" style={{ fontFamily: 'var(--hand)', fontSize: 'var(--t-md)', color: 'var(--ink-2)' }}>
              Sketch a part, scan the QR in the corner, and Factory OS reads the page — every material call, requirement, and open question becomes a structured, searchable part of the record. Try it below.
            </p>
          </div>

          <ScanToNode />

          <div className="nb-sticky mt-8" style={{ transform: 'rotate(-0.4deg)' }}>
            <b>⚡ That bracket is the same node</b> the homepage revision history is built around — Rev A's fatigue failure at this fillet, and the Rev B fix, both start from a page like this one. Not a blank page.
          </div>
        </div>
      </section>

      {/* ══════════ HOW IT WORKS ══════════ */}
      <section className="py-12 md:py-[64px] px-5" style={{ borderTop: '1px dashed var(--ink-4)' }}>
        <div className="max-w-[1080px] mx-auto">
          <div className="max-w-[720px] mb-7">
            <div className="nb-eyebrow">how it works</div>
            <h2 className="mt-2" style={{ fontFamily: 'var(--hand)', fontWeight: 700, fontSize: 'var(--t-lg)', lineHeight: 1.1, color: 'var(--ink)' }}>
              Three steps. No keyboard required.
            </h2>
          </div>

          <ol className="grid grid-cols-1 md:grid-cols-3 gap-3 list-none p-0 m-0">
            {[
              ['01', 'Sketch it', 'Work the way you already do — graph, sketch, or dot paper. Draw the part, note the material, jot the requirement.'],
              ['02', 'Scan the QR', 'Every page has its own code. Scan it with your phone and the photo goes to the Factory OS OCR pipeline.'],
              ['03', 'It becomes a node', "The page comes back structured — part, material, requirement, rationale — connected to everything else you've captured."],
            ].map(([n, title, body]) => (
              <li key={n} className="nb-sketch-thin p-4">
                <div style={{ ...mono('var(--t-sm)', ACCENT), fontWeight: 700, letterSpacing: '1px' }}>{n}</div>
                <div style={{ fontFamily: 'var(--hand)', fontWeight: 700, fontSize: 'var(--t-md)', color: 'var(--ink)', marginTop: 6 }}>{title}</div>
                <div style={{ fontFamily: 'var(--hand)', fontSize: 'var(--t-md)', color: 'var(--ink-2)', lineHeight: 1.4, marginTop: 5 }}>{body}</div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* ══════════ THE OFFER ══════════ */}
      <section className="py-12 md:py-[64px] px-5" style={{ borderTop: '1px dashed var(--ink-4)' }}>
        <div className="max-w-[1080px] mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-[1.1fr_1fr] gap-8 items-start">
            <div>
              <div className="nb-eyebrow" style={{ color: GREEN }}>the offer</div>
              <h2 className="mt-2" style={{ fontFamily: 'var(--hand)', fontWeight: 700, fontSize: 'var(--t-lg)', lineHeight: 1.05, color: 'var(--ink)' }}>
                Your first 100 nodes<br />are free.
              </h2>
              <p className="mt-4 max-w-[520px]" style={{ fontFamily: 'var(--hand)', fontSize: 'var(--t-md)', color: 'var(--ink-2)', lineHeight: 1.45 }}>
                Every organization starts free — not a trial that expires, a real allowance that fits a first hardware program. Capture a hundred parts before you ever think about a plan. We never delete them.
              </p>

              <div className="mt-6 flex flex-col gap-2.5">
                {[
                  'A node is a part, sub-assembly, assembly, or product.',
                  'Revisions are unlimited and never count against your total.',
                  'Private by default — your IP and design data stay yours.',
                  'Free for university teams with a .edu email.',
                ].map((t) => (
                  <div key={t} className="flex items-start gap-2.5" style={{ fontFamily: 'var(--hand)', fontSize: 'var(--t-md)', color: 'var(--ink)', lineHeight: 1.4 }}>
                    <span style={{ ...mono('var(--t-sm)', GREEN), fontWeight: 700, marginTop: 1 }}>✓</span>
                    {t}
                  </div>
                ))}
              </div>

              <div className="flex flex-wrap gap-3 mt-7">
                <a href={REQUEST_ACCESS_URL} className="nb-btn no-underline text-center">Request free access</a>
              </div>
              <div className="mt-3" style={{ ...mono('var(--t-sm)', 'var(--ink-3)'), letterSpacing: '0.3px' }}>
                then $250/user/mo · up to 2,500 nodes · startups under five engineers $50
              </div>
            </div>

            {/* Node meter */}
            <div className="nb-sketch-thin p-5" style={{ transform: 'rotate(0.3deg)' }}>
              <div style={{ ...mono('var(--t-sm)', 'var(--ink-3)'), letterSpacing: '0.5px', textTransform: 'uppercase' }}>your organization</div>
              <div className="flex items-baseline gap-2 mt-1">
                <span style={{ fontFamily: 'var(--hand)', fontWeight: 700, fontSize: 'var(--t-lg)', color: 'var(--ink)', lineHeight: 1 }}>0</span>
                <span style={{ fontFamily: 'var(--hand)', fontSize: 'var(--t-md)', color: 'var(--ink-3)' }}>/ 100 nodes</span>
              </div>
              <div style={{ height: 10, borderRadius: 6, background: 'var(--paper-2)', border: '1px solid var(--ink-4)', marginTop: 12, overflow: 'hidden' }}>
                <div style={{ width: '2%', height: '100%', background: GREEN }} />
              </div>
              <div style={{ ...mono('var(--t-sm)', 'var(--ink-3)'), marginTop: 8 }}>Your first scan becomes node #1.</div>
              <div style={{ ...mono('var(--t-sm)', GREEN), fontWeight: 700, marginTop: 10 }}>100 free nodes to start.</div>
              <div style={{ borderTop: '1px dashed var(--ink-4)', marginTop: 13, paddingTop: 11, fontFamily: 'var(--hand)', fontSize: 'var(--t-md)', color: 'var(--ink-2)', lineHeight: 1.4 }}>
                At 100, the record goes read-only until you pick a plan. Nothing is ever deleted.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════ CLOSER ══════════ */}
      <section className="py-12 md:py-[72px] px-5 text-center" style={{ borderTop: '1px dashed var(--ink-4)' }}>
        <div className="max-w-[820px] mx-auto">
          <h2 style={{ fontFamily: 'var(--hand)', fontWeight: 700, fontSize: 'var(--t-lg)', lineHeight: 1.1, color: 'var(--ink)', marginBottom: 14 }}>
            The notebook is where it starts.
          </h2>
          <p className="mb-7" style={{ fontFamily: 'var(--script)', fontSize: 'var(--t-md)', color: ACCENT }}>
            Factory OS is where it compounds.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <a href={REQUEST_ACCESS_URL} className="nb-btn no-underline text-center">Request your free notebook</a>
            <a href="/" className="nb-btn nb-btn-ghost">See the whole system</a>
          </div>
        </div>
      </section>

      <LegalFooter />
    </div>
  );
};

export default StartPage;
